import { createAsyncThunk } from '@reduxjs/toolkit';

import axiosInstance, { isAxiosError, isCancel } from '@/utils/axios';
import { hospitalHeaders } from '@/utils';
import type {
    CreatePatientFlagRequest,
    DeletePatientFlagResponse,
    FlagDefinition,
    PatientFlagEmbed,
    PatientFlagsListResponse,
    PatientFlagsPagination,
    SetPatientFlagAssignmentRequest,
    UpdatePatientFlagRequest
} from '@/types/patient-flag';
import { getErrorMessage } from '@/utils/error-message';
import { resolveNextPatientFlags } from '@/utils/patient-flags/assignment';
import type { PatientFlagsState } from './types';

type PatientsFlagsLookupState = {
    meetingViewPatients: Array<{ id: string; flags?: PatientFlagEmbed[] }>;
    patients: Array<{ id: string; flags?: PatientFlagEmbed[] }>;
};

export type PatientFlagsRejectValue = {
    message: string;
    status?: number;
};

const toRejectValue = (error: unknown, fallbackMessage: string): PatientFlagsRejectValue => ({
    message: getErrorMessage(error, fallbackMessage),
    status: isAxiosError(error) ? error.response?.status : undefined
});

export type FetchPatientFlagsArg = {
    hospitalId?: string;
    search?: string;
    offset?: number;
    limit?: number;
};

export type FetchPatientFlagsResult = {
    items: FlagDefinition[];
    pagination: PatientFlagsPagination | null;
};

/** Default page size for searchable flag lists (Hospital Config / Manage). */
export const PATIENT_FLAGS_PAGE_LIMIT = 50;

const flagsCacheKeyForArg = (arg: FetchPatientFlagsArg | undefined) =>
    [
        arg?.hospitalId ?? 'default',
        arg?.search?.trim() ?? '',
        String(arg?.offset ?? 0),
        String(arg?.limit ?? PATIENT_FLAGS_PAGE_LIMIT)
    ].join('|');

/** `GET /patient-flags?offset&limit&search` — optional `X-Hospital-Id`. */
export const fetchPatientFlags = createAsyncThunk<
    FetchPatientFlagsResult,
    FetchPatientFlagsArg | undefined,
    { rejectValue: PatientFlagsRejectValue }
>(
    'patientFlags/fetchAll',
    async (arg, { rejectWithValue, signal }) => {
        try {
            const search = arg?.search?.trim() || undefined;
            const { data } = await axiosInstance.get<PatientFlagsListResponse | FlagDefinition[]>('patient-flags', {
                signal,
                params: {
                    search,
                    offset: arg?.offset ?? 0,
                    limit: arg?.limit ?? PATIENT_FLAGS_PAGE_LIMIT
                },
                ...hospitalHeaders(arg?.hospitalId)
            });
            if (Array.isArray(data)) {
                return { items: data, pagination: null };
            }
            return {
                items: data?.items ?? [],
                pagination: data?.pagination ?? null
            };
        } catch (error: unknown) {
            if (isCancel(error)) {
                throw error;
            }
            return rejectWithValue(toRejectValue(error, 'Failed to load patient flags'));
        }
    },
    {
        condition: (arg, { getState }) => {
            const { patientFlags } = getState() as { patientFlags: PatientFlagsState };
            const key = flagsCacheKeyForArg(arg);
            if (patientFlags.definitionsLoading) {
                return false;
            }
            if (patientFlags.definitions.length > 0 && patientFlags.definitionsCacheKey === key) {
                return false;
            }
            return true;
        }
    }
);

export type CreatePatientFlagArg = {
    body: CreatePatientFlagRequest;
    hospitalId?: string;
};

/** `POST /patient-flags` — custom flags only. */
export const createPatientFlag = createAsyncThunk<FlagDefinition, CreatePatientFlagArg, { rejectValue: PatientFlagsRejectValue }>(
    'patientFlags/create',
    async ({ body, hospitalId }, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post<FlagDefinition>('patient-flags', body, hospitalHeaders(hospitalId));
            return data;
        } catch (error: unknown) {
            return rejectWithValue(toRejectValue(error, 'Failed to create patient flag'));
        }
    }
);

export type UpdatePatientFlagArg = {
    flagId: string;
    body: UpdatePatientFlagRequest;
    hospitalId?: string;
};

const asFlagDefinition = (data: unknown): Partial<FlagDefinition> => {
    if (!data || typeof data !== 'object') {
        return {};
    }
    const record = data as Record<string, unknown>;
    const nested =
        record.flag && typeof record.flag === 'object'
            ? (record.flag as Record<string, unknown>)
            : record.item && typeof record.item === 'object'
              ? (record.item as Record<string, unknown>)
              : record;
    return {
        ...(typeof nested.id === 'string' ? { id: nested.id } : {}),
        ...(typeof nested.name === 'string' ? { name: nested.name } : {}),
        ...(typeof nested.color === 'string' ? { color: nested.color } : {}),
        ...(typeof nested.isSystem === 'boolean' ? { isSystem: nested.isSystem } : {}),
        ...(typeof nested.autoCompute === 'boolean' ? { autoCompute: nested.autoCompute } : {}),
        ...(nested.systemKey === null || typeof nested.systemKey === 'string'
            ? { systemKey: nested.systemKey as FlagDefinition['systemKey'] }
            : {})
    };
};

/** `PATCH /patient-flags/:flagId` — custom flags only. */
export const updatePatientFlag = createAsyncThunk<FlagDefinition, UpdatePatientFlagArg, { rejectValue: PatientFlagsRejectValue }>(
    'patientFlags/update',
    async ({ flagId, body, hospitalId }, { getState, rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.patch<unknown>(`patient-flags/${flagId}`, body, hospitalHeaders(hospitalId));
            const { patientFlags } = getState() as { patientFlags: PatientFlagsState };
            const existing = patientFlags.definitions.find((flag) => flag.id === flagId);
            const fromResponse = asFlagDefinition(data);
            if (!existing && !fromResponse.name && !body.name) {
                return rejectWithValue({ message: 'Updated flag response was incomplete.' });
            }
            return {
                id: flagId,
                name: fromResponse.name ?? body.name ?? existing?.name ?? '',
                color: fromResponse.color ?? body.color ?? existing?.color ?? '',
                isSystem: fromResponse.isSystem ?? existing?.isSystem ?? false,
                autoCompute: fromResponse.autoCompute ?? existing?.autoCompute ?? false,
                systemKey: fromResponse.systemKey !== undefined ? fromResponse.systemKey : (existing?.systemKey ?? null)
            };
        } catch (error: unknown) {
            return rejectWithValue(toRejectValue(error, 'Failed to update patient flag'));
        }
    }
);

export type DeletePatientFlagArg = {
    flagId: string;
    hospitalId?: string;
};

/** `DELETE /patient-flags/:flagId` — soft-deletes custom flag + assignments. */
export const deletePatientFlag = createAsyncThunk<
    DeletePatientFlagResponse,
    DeletePatientFlagArg,
    { rejectValue: PatientFlagsRejectValue }
>('patientFlags/delete', async ({ flagId, hospitalId }, { rejectWithValue }) => {
    try {
        const { data } = await axiosInstance.delete<DeletePatientFlagResponse>(`patient-flags/${flagId}`, hospitalHeaders(hospitalId));
        return data;
    } catch (error: unknown) {
        return rejectWithValue(toRejectValue(error, 'Failed to delete patient flag'));
    }
});

export type SetPatientFlagAssignmentArg = {
    patientId: string;
    flagId: string;
    body: SetPatientFlagAssignmentRequest;
    hospitalId?: string;
};

export type SetPatientFlagAssignmentResult = {
    patientId: string;
    flags: PatientFlagEmbed[];
};

/** `PUT /patient-flags/patients/:patientId/:flagId` — assign / unassign. */
export const setPatientFlagAssignment = createAsyncThunk<
    SetPatientFlagAssignmentResult,
    SetPatientFlagAssignmentArg,
    { rejectValue: PatientFlagsRejectValue }
>('patientFlags/setAssignment', async ({ patientId, flagId, body, hospitalId }, { getState, rejectWithValue }) => {
    try {
        const { data } = await axiosInstance.put<unknown>(
            `patient-flags/patients/${patientId}/${flagId}`,
            body,
            hospitalHeaders(hospitalId)
        );
        const rootState = getState() as { patients: PatientsFlagsLookupState; patientFlags: PatientFlagsState };
        const patient =
            rootState.patients?.meetingViewPatients?.find((item) => item.id === patientId) ??
            rootState.patients?.patients?.find((item) => item.id === patientId);
        const flags = resolveNextPatientFlags({
            data,
            flagId,
            assigned: body.assigned,
            previousFlags: patient?.flags ?? [],
            definitions: rootState.patientFlags.definitions
        });
        return { patientId, flags };
    } catch (error: unknown) {
        return rejectWithValue(toRejectValue(error, 'Failed to update patient flag assignment'));
    }
});
